<script lang="ts">
  /**
   * Right-slide-in panel showing the full record for `selection.selectedId`.
   *
   * Auto-fetches when the selected id changes. Closes via the X button or
   * Escape key. Pushes the selection back to the host via reportSelection
   * when running iframed inside DC.
   */

  import { selection, writeMode, trail, clearTrail, undoTrail, pushTrailStep } from './selection.svelte'
  import {
    fetchMemoryDetail, updateMemory, deleteMemory, apiStatus,
    WritesDisabledError, type MemoryDetail,
  } from './api.svelte'
  import { reportSelection } from './addonProtocol.svelte'
  import { mco } from './mcoAuth.svelte'
  import ContextDialog from './ContextDialog.svelte'
  import Attachments from './Attachments.svelte'

  let detail = $state<MemoryDetail | null>(null)
  let loading = $state(false)
  let error = $state<string | null>(null)

  /* Edit state for in-place importance + topic. */
  let editingImportance = $state(false)
  let editingTopic = $state(false)
  let importanceDraft = $state(0)
  let topicDraft = $state('')
  let saving = $state(false)
  let confirmDelete = $state(false)
  let deleting = $state(false)
  let contextOpen = $state(false)

  // viewing an agent's memory (ticket #9) is READ-ONLY — mu-api refuses writes there too
  const writeAvailable = $derived(writeMode.unlocked && apiStatus.writesEnabled && !mco.viewing)

  /* Refetch when selectedId changes. Bail if it's null (panel closes). */
  $effect(() => {
    const id = selection.selectedId
    if (id === null) {
      detail = null
      error = null
      editingImportance = false
      editingTopic = false
      confirmDelete = false
      return
    }
    loading = true
    error = null
    fetchMemoryDetail(id)
      .then((d) => {
        detail = d
        if (d == null) error = `Memory ${id} not found`
        else reportSelection(id)
      })
      .catch((e) => { error = (e as Error).message })
      .finally(() => { loading = false })
  })

  function close(): void {
    selection.selectedId = null
  }

  async function saveImportance(): Promise<void> {
    if (!detail || saving) return
    const next = Math.max(0, Math.min(25, Math.round(importanceDraft)))
    if (next === detail.importance) {
      editingImportance = false
      return
    }
    saving = true
    try {
      detail = await updateMemory(detail.id, { importance: next })
      editingImportance = false
    } catch (err) {
      error = err instanceof WritesDisabledError ? 'writes disabled on api' : (err as Error).message
    } finally {
      saving = false
    }
  }

  async function saveTopic(): Promise<void> {
    if (!detail || saving) return
    const next = topicDraft.trim() || null
    if (next === detail.topic) {
      editingTopic = false
      return
    }
    saving = true
    try {
      detail = await updateMemory(detail.id, { topic: next })
      editingTopic = false
    } catch (err) {
      error = err instanceof WritesDisabledError ? 'writes disabled on api' : (err as Error).message
    } finally {
      saving = false
    }
  }

  function followStrongest(): void {
    if (!detail || !detail.relationshipsList) return
    const visitedRecently = new Set(trail.steps.slice(-10).map((s) => s.id))
    /* Pick the strongest connection that's not in the recent trail (avoids
     * ping-pong between two strongly-connected memories). Falls back to
     * strongest overall if all relationships are recently visited. */
    const sorted = [...detail.relationshipsList].sort((a, b) => b.strength - a.strength)
    const next = sorted.find((r) => !visitedRecently.has(r.connectedId)) ?? sorted[0]
    if (!next) return
    /* Seed trail with current memory if empty so the polyline shows a
     * starting point. */
    if (trail.steps.length === 0 && detail.id != null) {
      pushTrailStep(detail.id, 'manual')
    }
    pushTrailStep(next.connectedId, 'follow')
    selection.selectedId = next.connectedId
  }

  async function performDelete(): Promise<void> {
    if (!detail || deleting) return
    deleting = true
    try {
      await deleteMemory(detail.id)
      /* Closing the panel triggers the selection clear; the constellation
       * picks up the layoutCache invalidation on its next refresh. */
      close()
    } catch (err) {
      error = err instanceof WritesDisabledError ? 'writes disabled on api' : (err as Error).message
    } finally {
      deleting = false
      confirmDelete = false
    }
  }

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape' && selection.selectedId !== null) {
      e.preventDefault()
      close()
    }
  }

  function importanceTier(n: number | null | undefined): string {
    const v = n ?? 0
    if (v >= 20) return 'APEX'
    if (v >= 15) return 'ELITE'
    if (v >= 5)  return 'HIGH'
    if (v >= 0)  return 'BASE'
    return 'LOW'
  }

  function fmtTs(ts: string | null | undefined): string {
    if (!ts) return '—'
    try {
      const d = new Date(ts)
      return d.toISOString().slice(0, 19).replace('T', ' ')
    } catch {
      return String(ts)
    }
  }
</script>

<svelte:window onkeydown={onKey} />

<ContextDialog open={contextOpen} sourceId={selection.selectedId} onclose={() => contextOpen = false} />

{#if selection.selectedId !== null}
  <aside class="md-panel" aria-label="Memory detail">
    <header class="md-head">
      <span class="md-tag">// memory</span>
      <span class="md-id">#{selection.selectedId}</span>
      <button class="md-close" type="button" onclick={close} aria-label="Close">✕</button>
    </header>

    {#if loading}
      <div class="md-body md-loading">FETCHING…</div>
    {:else if error}
      <div class="md-body md-error">{error}</div>
    {:else if detail}
      <div class="md-body">
        <div class="md-row md-row-topic">
          <span class="md-label">topic</span>
          {#if editingTopic}
            <input
              class="md-edit-input"
              type="text"
              bind:value={topicDraft}
              onblur={() => void saveTopic()}
              onkeydown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); void saveTopic() }
                else if (e.key === 'Escape') { editingTopic = false }
              }}
              autofocus
            />
          {:else}
            <button
              class="md-value md-topic md-editable"
              type="button"
              disabled={!writeAvailable}
              onclick={() => {
                if (!writeAvailable || !detail) return
                topicDraft = detail.topic ?? ''
                editingTopic = true
              }}
              title={writeAvailable ? 'Click to edit topic' : ''}
            >{detail.topic ?? '(none)'}</button>
          {/if}
        </div>

        <div class="md-row md-row-meta">
          <span class="md-pill md-tier">{importanceTier(detail.importance)}</span>
          {#if editingImportance}
            <input
              class="md-edit-imp"
              type="number"
              min="0" max="25" step="1"
              bind:value={importanceDraft}
              onblur={() => void saveImportance()}
              onkeydown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); void saveImportance() }
                else if (e.key === 'Escape') { editingImportance = false }
              }}
              autofocus
            />
          {:else}
            <button
              class="md-pill md-editable"
              type="button"
              disabled={!writeAvailable}
              onclick={() => {
                if (!writeAvailable || !detail) return
                importanceDraft = detail.importance ?? 0
                editingImportance = true
              }}
              title={writeAvailable ? 'Click to edit importance' : ''}
            >imp {detail.importance ?? 0}</button>
          {/if}
          {#if detail.hasEmbedding}
            <span class="md-pill md-pill-on">embed</span>
          {:else}
            <span class="md-pill md-pill-off">no-embed</span>
          {/if}
          <span class="md-pill">in {detail.relationships.in}</span>
          <span class="md-pill">out {detail.relationships.out}</span>
        </div>

        <div class="md-row">
          <span class="md-label">type</span>
          <span class="md-value">{detail.memoryType ?? '—'}</span>
        </div>
        <div class="md-row">
          <span class="md-label">agent</span>
          <span class="md-value">{detail.agentId ?? '—'}</span>
        </div>
        <div class="md-row">
          <span class="md-label">stamp</span>
          <span class="md-value">{fmtTs(detail.timestamp)}</span>
        </div>
        <div class="md-row">
          <span class="md-label">access</span>
          <span class="md-value">
            {detail.accessCount}× · last {fmtTs(detail.lastAccessed)}
          </span>
        </div>

        {#if detail.tags.length > 0}
          <div class="md-row md-row-tags">
            <span class="md-label">tags</span>
            <div class="md-tags">
              {#each detail.tags as t (t)}
                <span class="md-tag-chip">{t}</span>
              {/each}
            </div>
          </div>
        {/if}

        {#if detail.v2Enrichment}
          <div class="md-row md-row-v2">
            <span class="md-label">v2</span>
            <div class="md-v2">
              {#each Object.entries(detail.v2Enrichment) as [k, v] (k)}
                <span class="md-v2-pair">
                  <span class="md-v2-k">{k}</span>
                  <span class="md-v2-v">{String(v).slice(0, 24)}</span>
                </span>
              {/each}
            </div>
          </div>
        {/if}

        <div class="md-section">
          <header class="md-section-head">
            <span class="md-label">preview</span>
            <span class="md-section-meta">
              {detail.preview.length} / {detail.contentLength.toLocaleString()} chars
            </span>
          </header>
          <pre class="md-content">{detail.preview}{detail.contentLength > detail.preview.length ? '…' : ''}</pre>
          <div class="md-content-actions">
            <button
              class="md-btn md-btn-context"
              type="button"
              onclick={() => contextOpen = true}
              title="View this memory + all its sibling chunks (parent_id walk + topic-stem match)"
            >📖 FULL CONTEXT</button>
          </div>
        </div>

        {#if detail.relationshipsList && detail.relationshipsList.length > 0}
          <div class="md-section">
            <header class="md-section-head">
              <span class="md-label">relationships</span>
              <span class="md-section-meta">
                {detail.relationshipsList.length}
                {#if (detail.relationships.in + detail.relationships.out) > detail.relationshipsList.length}
                  / {detail.relationships.in + detail.relationships.out}
                {/if}
              </span>
              <button
                class="md-follow-btn"
                type="button"
                onclick={followStrongest}
                title="Walk to the strongest unvisited connection"
              >▶ FOLLOW</button>
            </header>
            <div class="md-rel-list">
              {#each detail.relationshipsList as r (r.direction + ':' + r.connectedId)}
                <button
                  class="md-rel-item"
                  type="button"
                  onclick={() => {
                    /* Relationship-row navigation IS an intentional step,
                     * so push to trail. Manual point clicks on the
                     * constellation no longer auto-track. */
                    pushTrailStep(r.connectedId, 'manual')
                    selection.selectedId = r.connectedId
                  }}
                  title="Jump to memory #{r.connectedId}"
                >
                  <span class="md-rel-arrow" data-dir={r.direction}>{r.direction === 'out' ? '→' : '←'}</span>
                  <span class="md-rel-id">#{r.connectedId}</span>
                  <span class="md-rel-type">{r.type}</span>
                  <span class="md-rel-strength">{Math.round(r.strength * 100)}%</span>
                  <span class="md-rel-topic">{r.connectedTopic ?? '(no topic)'}</span>
                </button>
              {/each}
            </div>
          </div>
        {/if}

        <Attachments {detail} {writeAvailable} onUpdate={(next) => { detail = next }} />

        {#if trail.steps.length > 1}
          <nav class="md-trail" aria-label="Memory walk trail">
            <span class="md-trail-tag">// trail</span>
            <button class="md-trail-act" type="button" onclick={undoTrail} title="Step back">↶</button>
            <button class="md-trail-act" type="button" onclick={clearTrail} title="Clear trail">✕</button>
            <div class="md-trail-list">
              {#each trail.steps.slice(-12) as s, i (s.ts + ':' + s.id)}
                {@const stepIsLast = i === trail.steps.slice(-12).length - 1}
                <button
                  class="md-trail-step"
                  class:current={s.id === selection.selectedId}
                  class:via-follow={s.via === 'follow'}
                  type="button"
                  onclick={() => { selection.selectedId = s.id }}
                  title="#{s.id} · via {s.via}"
                >#{s.id}</button>
                {#if !stepIsLast}<span class="md-trail-arrow">→</span>{/if}
              {/each}
              {#if trail.steps.length > 12}
                <span class="md-trail-more">+{trail.steps.length - 12} earlier</span>
              {/if}
            </div>
          </nav>
        {/if}

        {#if writeAvailable}
          <footer class="md-footer">
            {#if confirmDelete}
              <span class="md-confirm-text">Delete this memory?</span>
              <button class="md-btn md-btn-cancel" type="button" onclick={() => confirmDelete = false}>CANCEL</button>
              <button class="md-btn md-btn-danger" type="button" disabled={deleting} onclick={() => void performDelete()}>
                {deleting ? 'DELETING…' : 'DELETE'}
              </button>
            {:else}
              <span class="md-write-hint">// click topic / importance to edit</span>
              <button class="md-btn md-btn-danger md-btn-soft" type="button" onclick={() => confirmDelete = true}>
                DELETE
              </button>
            {/if}
          </footer>
        {/if}
      </div>
    {/if}
  </aside>
{/if}

<style>
  .md-panel {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(420px, 90vw);
    background: var(--surface);
    border-left: 1px solid var(--accent-soft);
    box-shadow: -8px 0 32px rgba(0, 0, 0, 0.6);
    display: flex;
    flex-direction: column;
    font-family: var(--font-mono);
    z-index: 50;
    animation: md-in 180ms ease-out;
  }
  @keyframes md-in {
    from { transform: translateX(20%); opacity: 0; }
    to   { transform: translateX(0);   opacity: 1; }
  }

  .md-head {
    display: flex;
    align-items: center;
    gap: var(--s-2);
    padding: var(--s-2) var(--s-3);
    border-bottom: 1px solid var(--outline-variant);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    flex-shrink: 0;
  }
  .md-tag { color: var(--accent); font-weight: 700; }
  .md-id  { color: var(--on-surface); flex: 1; font-weight: 600; }
  .md-close {
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    width: 24px; height: 24px;
    cursor: pointer;
    line-height: 1;
    transition: color var(--t-fast), border-color var(--t-fast);
  }
  .md-close:hover { color: var(--accent); border-color: var(--accent); }

  .md-body {
    padding: var(--s-3);
    overflow-y: auto;
    flex: 1;
    font-size: 11px;
  }
  .md-loading, .md-error {
    color: var(--on-surface-variant);
    letter-spacing: 0.12em;
    text-transform: uppercase;
    text-align: center;
    padding: var(--s-6) 0;
  }
  .md-error { color: #ff6b6b; }

  .md-row {
    display: flex;
    gap: var(--s-2);
    padding: 4px 0;
    align-items: baseline;
  }
  .md-row-topic .md-value { font-weight: 600; }
  .md-label {
    width: 56px;
    flex-shrink: 0;
    color: var(--on-surface-faint);
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .md-value {
    color: var(--on-surface);
    word-break: break-word;
  }
  .md-topic {
    color: var(--accent-bright);
  }

  .md-row-meta {
    flex-wrap: wrap;
    margin: var(--s-2) 0;
  }
  .md-pill {
    display: inline-block;
    padding: 2px 6px;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    background: var(--surface-container-low);
  }
  .md-pill-on { color: var(--accent); border-color: var(--accent-soft); }
  .md-pill-off { color: var(--on-surface-faint); border-style: dashed; }
  .md-tier { color: var(--accent-bright); font-weight: 700; border-color: var(--accent); }

  .md-tags { display: flex; flex-wrap: wrap; gap: 4px; }
  .md-tag-chip {
    padding: 1px 5px;
    background: var(--surface-container);
    color: var(--on-surface);
    font-size: 10px;
    border: 1px solid var(--outline-variant);
  }

  .md-row-v2 .md-v2 {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    flex: 1;
  }
  .md-v2-pair {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    padding: 1px 6px;
    border: 1px solid var(--outline-variant);
    background: var(--surface-container-low);
    font-size: 10px;
  }
  .md-v2-k { color: var(--on-surface-faint); }
  .md-v2-v { color: var(--accent); }

  .md-section { margin-top: var(--s-3); }
  .md-section-head {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
    border-top: 1px dashed var(--outline-variant);
    padding-top: var(--s-2);
  }
  .md-section-meta {
    font-size: 9px;
    color: var(--on-surface-faint);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .md-content {
    margin: 0;
    padding: var(--s-2);
    background: var(--surface-container-lowest);
    border: 1px solid var(--outline-variant);
    font-family: inherit;
    font-size: 10px;
    line-height: 1.5;
    color: var(--on-surface);
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 50vh;
    overflow-y: auto;
  }

  .md-content-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 4px;
  }
  .md-btn-context {
    font: inherit;
    font-size: 10px;
    letter-spacing: 0.08em;
    padding: 4px 10px;
    color: var(--accent);
    background: transparent;
    border: 1px solid var(--accent-soft);
    cursor: pointer;
    transition: background var(--t-fast), color var(--t-fast);
  }
  .md-btn-context:hover {
    background: var(--accent-faint);
    color: var(--accent-bright);
  }

  /* ── Editable affordances ────────────────────────────────────── */
  .md-editable {
    cursor: pointer;
    font: inherit;
    text-align: left;
    color: inherit;
    background: transparent;
    border: 1px dashed transparent;
    padding: 1px 4px;
    transition: border-color var(--t-fast), background var(--t-fast);
  }
  .md-editable:not(:disabled):hover {
    border-color: var(--accent-soft);
    background: var(--accent-faint);
  }
  .md-editable:disabled {
    cursor: default;
    border-color: transparent;
  }
  .md-edit-input {
    flex: 1;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--accent-bright);
    background: var(--surface-container);
    border: 1px solid var(--accent);
    padding: 2px 6px;
    outline: none;
  }
  .md-edit-imp {
    width: 60px;
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--accent-bright);
    background: var(--surface-container);
    border: 1px solid var(--accent);
    padding: 1px 4px;
    outline: none;
    text-align: center;
  }

  /* ── Relationships list ─────────────────────────────────────── */
  .md-rel-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-height: 36vh;
    overflow-y: auto;
    border: 1px solid var(--outline-variant);
    background: var(--surface-container-lowest);
    padding: 4px;
  }
  .md-rel-item {
    display: grid;
    grid-template-columns: 16px 56px 1fr auto auto;
    gap: 6px;
    align-items: center;
    width: 100%;
    text-align: left;
    padding: 4px 6px;
    background: transparent;
    border: 1px solid transparent;
    color: var(--on-surface);
    font-family: inherit;
    font-size: 10px;
    cursor: pointer;
    transition: background var(--t-fast), border-color var(--t-fast);
  }
  .md-rel-item:hover {
    background: var(--surface-container);
    border-color: var(--accent-soft);
  }
  .md-rel-arrow {
    color: var(--accent);
    font-size: 12px;
    font-weight: 700;
  }
  .md-rel-arrow[data-dir='in'] { color: var(--on-surface-variant); }
  .md-rel-id {
    color: var(--accent);
    font-weight: 600;
  }
  .md-rel-type {
    color: var(--on-surface-variant);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-transform: lowercase;
    letter-spacing: 0.04em;
  }
  .md-rel-strength {
    color: var(--accent-bright);
    font-weight: 600;
    text-align: right;
    min-width: 36px;
  }
  .md-rel-topic {
    grid-column: 3 / span 3;
    color: var(--on-surface);
    font-size: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* ── Follow button (sequence walk) ──────────────────────────── */
  .md-follow-btn {
    background: transparent;
    border: 1px solid var(--accent);
    color: var(--accent);
    font: inherit;
    font-size: 9px;
    letter-spacing: 0.1em;
    padding: 2px 8px;
    cursor: pointer;
    margin-left: var(--s-2);
    transition: background var(--t-fast), color var(--t-fast);
  }
  .md-follow-btn:hover {
    background: var(--accent);
    color: var(--surface);
  }

  /* ── Trail breadcrumbs ──────────────────────────────────────── */
  .md-trail {
    margin-top: var(--s-3);
    padding-top: var(--s-2);
    border-top: 1px dashed var(--outline-variant);
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
    font-size: 10px;
  }
  .md-trail-tag {
    color: var(--accent);
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .md-trail-act {
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-faint);
    width: 22px; height: 22px;
    line-height: 1;
    cursor: pointer;
    transition: color var(--t-fast), border-color var(--t-fast);
  }
  .md-trail-act:hover { color: var(--accent); border-color: var(--accent); }
  .md-trail-list {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 2px;
    flex: 1;
  }
  .md-trail-step {
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    font: inherit;
    font-size: 10px;
    padding: 2px 6px;
    cursor: pointer;
    transition: color var(--t-fast), border-color var(--t-fast), background var(--t-fast);
  }
  .md-trail-step:hover { color: var(--accent); border-color: var(--accent-soft); }
  .md-trail-step.current {
    color: var(--accent-bright);
    border-color: var(--accent);
    background: var(--accent-faint);
  }
  .md-trail-step.via-follow {
    border-style: dashed;
    border-color: var(--accent-soft);
    color: var(--accent);
  }
  .md-trail-arrow {
    color: var(--on-surface-faint);
    font-size: 10px;
  }
  .md-trail-more {
    color: var(--on-surface-faint);
    font-size: 9px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-left: var(--s-2);
  }

  /* ── Delete footer ──────────────────────────────────────────── */
  .md-footer {
    margin-top: var(--s-3);
    padding-top: var(--s-2);
    border-top: 1px dashed var(--outline-variant);
    display: flex;
    align-items: center;
    gap: var(--s-2);
    font-size: 10px;
  }
  .md-write-hint {
    flex: 1;
    color: var(--on-surface-faint);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .md-confirm-text {
    flex: 1;
    color: #ff8b8b;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-weight: 600;
  }
  .md-btn {
    font: inherit;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 4px 10px;
    background: transparent;
    color: var(--on-surface-variant);
    border: 1px solid var(--outline-variant);
    cursor: pointer;
    transition: color var(--t-fast), border-color var(--t-fast), background var(--t-fast);
  }
  .md-btn:hover { color: var(--accent); border-color: var(--accent); }
  .md-btn-cancel { color: var(--on-surface); }
  .md-btn-danger {
    color: #ff6b6b;
    border-color: #ff6b6b;
  }
  .md-btn-danger:hover {
    background: rgba(255, 107, 107, 0.1);
    color: #ff8b8b;
  }
  .md-btn-soft {
    color: var(--on-surface-faint);
    border-color: var(--outline-variant);
    border-style: dashed;
  }
  .md-btn-soft:hover {
    color: #ff6b6b;
    border-color: #ff6b6b;
    border-style: solid;
  }
  .md-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
